import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from '../../../offices/offices.styles';
import OfficeSelectionCard from '../cards/office-selection-card.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../../core/components/center/center.component';
import Column from '../../../../core/components/column/column.component';
import { CrossAxisAlignment } from '../../../../core/components/flex/flex.component';
import Row from '../../../../core/components/row/row.component';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import StyleConstants from '../../../../core/styles.constants';
import { useAdminOrAuthorOffices } from '../../../offices/hooks/offices.hook';
import type Office from '../../../offices/models/office';
import Grid from '../../../../core/components/grid/grid.component';

interface Props {
  readonly initiallySelectedLinkedOfficeIds: string[];
  readonly handleLinkedOfficeSelectionChanged: (linkedOffices: string[]) => void;
}

const LinkedOfficesGroup = ({ initiallySelectedLinkedOfficeIds, handleLinkedOfficeSelectionChanged }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [didFillData, setDidFillData] = useState(false);
  const [linkedOffices, setLinkedOffices] = useState<string[]>(initiallySelectedLinkedOfficeIds);

  const officesState = useAdminOrAuthorOffices();

  let sortedOffices: Office[] = [];

  if (officesState.offices != null) {
    sortedOffices = officesState.offices.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)) || [];
  }

  const handleLinkedOfficeChanged = (linkedOfficeIdToCreate: string): void => {
    if (linkedOffices.includes(linkedOfficeIdToCreate)) {
      linkedOffices.splice(linkedOffices.indexOf(linkedOfficeIdToCreate), 1);
    } else {
      linkedOffices.push(linkedOfficeIdToCreate);
    }

    setLinkedOffices(linkedOffices);
    handleLinkedOfficeSelectionChanged(linkedOffices);
  };

  if (linkedOffices && !didFillData) {
    setLinkedOffices([...initiallySelectedLinkedOfficeIds]);
    setDidFillData(true);
  }

  return (
    <div className={style(styles.inModalSectionCard(isMobile))}>
      <Column className={style(styles.headerMargin)}>
        <Row crossAxisAlignment={CrossAxisAlignment.Center}>
          <span className={style(styles.columnHeader(isMobile))}>{t('offices-using-facility')}</span>
        </Row>
        <div className={style(styles.horizontalDivider)} />

        {!officesState.initialized ? (
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        ) : (
          <Column>
            {sortedOffices.length === 0 ? (
              <span className={style(styles.emptyListMessage)}>{t('no-offices-linked')}</span>
            ) : (
              <Grid
                rowClass={style(styles.gridRow)}
                itemSpacing={12}
                numberOfColumns={isMobile ? 1 : 3}
                numberOfItems={sortedOffices.length}
                renderItem={(index): React.JSX.Element => {
                  const officeItem = sortedOffices[index];

                  return (
                    officeItem && (
                      <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={officeItem.id}>
                        <OfficeSelectionCard
                          office={officeItem}
                          isChecked={officeItem.id !== undefined && linkedOffices.includes(officeItem.id)}
                          onClick={handleLinkedOfficeChanged}
                        />
                      </div>
                    )
                  );
                }}
              />
            )}
          </Column>
        )}
      </Column>
    </div>
  );
};

export default LinkedOfficesGroup;
